import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_BASE_URL;

/* ---------------- FETCH BY COMPANY ---------------- */
export const fetchBranchesByCompanyId = createAsyncThunk(
  "branch/fetchByCompanyId",
  async ({ companyId, role }, { rejectWithValue }) => {
    try {
      let url = `${API_URL}/companies/branches/by-company/${companyId}`;
      const response = await axios.get(url);
      return response.data;
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to fetch branches";
      return rejectWithValue(Array.isArray(msg) ? msg.join(", ") : msg);
    }
  }
);

/* ---------------- FETCH BY HIERARCHY ---------------- */
export const fetchBranchesByHierarchy = createAsyncThunk(
  "branch/fetchByHierarchy",
  async ({ branchId }, { rejectWithValue }) => {
    try {
      let url = `${API_URL}/companies/hierarchy/${branchId}`;
      const response = await axios.get(url);
      return response.data; // Returns flat array [BranchA, SubBranch1, SubBranch2]
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to fetch branch hierarchy";
      return rejectWithValue(Array.isArray(msg) ? msg.join(", ") : msg);
    }
  }
);

/* ---------------- CREATE ---------------- */
export const saveBranch = createAsyncThunk(
  "branch/saveBranch",
  async ({ data, companyId, role }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_URL}/companies/branch`, data);
      dispatch(fetchBranchesByCompanyId({ companyId, role }));
      return response.data;
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to save branch";
      return rejectWithValue(Array.isArray(msg) ? msg.join(", ") : msg);
    }
  }
);

/* ---------------- UPDATE / DEACTIVATE ---------------- */
export const updateBranch = createAsyncThunk(
  "branch/updateBranch",
  async ({ id, data, companyId, role }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.patch(
        `${API_URL}/companies/branch/${id}`,
        data
      );
      dispatch(fetchBranchesByCompanyId({ companyId, role }));
      return response.data;
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to update branch";
      return rejectWithValue(Array.isArray(msg) ? msg.join(", ") : msg);
    }
  }
);

/* ---------------- SLICE ---------------- */
const branchSlice = createSlice({
  name: "branch",
  initialState: {
    list: [],
    status: "idle",
    error: null,
  },
  reducers: {
    clearBranchError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      /* Company Fetch Cases */
      .addCase(fetchBranchesByCompanyId.pending, (state) => { state.status = "loading"; })
      .addCase(fetchBranchesByCompanyId.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.list = action.payload;
      })
      .addCase(fetchBranchesByCompanyId.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      /* Hierarchy Fetch Cases */
      .addCase(fetchBranchesByHierarchy.pending, (state) => { 
        state.status = "loading"; 
        state.error = null;
      })
      .addCase(fetchBranchesByHierarchy.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.list = action.payload; // Overwrites options to match only current sub-tree
      })
      .addCase(fetchBranchesByHierarchy.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      /* UPDATE Cases */
      .addCase(updateBranch.fulfilled, (state) => {
        state.status = "succeeded";
        state.error = null;
      })
      .addCase(updateBranch.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { clearBranchError } = branchSlice.actions;
export default branchSlice.reducer;